import { MainSettingsItem } from 'src/shared/models/user';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';

type HelpPageTerm = {
  tag: string;
  key: MainSettingsEnum;
  defaultLabel: string;
};

// Begrepp som kunden kan byta namn på under Benämningar och väljare. Taggen med liten bokstav används mitt i en mening,
// samma tagg med stor bokstav i början av en mening. Standardorden är svenska oavsett språk i webben, eftersom
// artiklarna skrivs på svenska.
const helpPageTerms: HelpPageTerm[] = [
  { tag: 'förening', key: MainSettingsEnum.LocalLabel, defaultLabel: 'förening' },
  { tag: 'föreningar', key: MainSettingsEnum.LocalLabelPlural, defaultLabel: 'föreningar' },
  { tag: 'distrikt', key: MainSettingsEnum.CountyLabel, defaultLabel: 'distrikt' },
  { tag: 'intressent', key: MainSettingsEnum.OtherLabel, defaultLabel: 'intressent' },
  { tag: 'intressenter', key: MainSettingsEnum.OtherLabelPlural, defaultLabel: 'intressenter' },
  { tag: 'vänmedlem', key: MainSettingsEnum.NewFriendLabel, defaultLabel: 'vänmedlem' },
];

const capitalize = (word: string) => word.charAt(0).toLocaleUpperCase('sv') + word.slice(1);

// Första bokstaven blir liten bara om den andra också är liten, så att förkortningar som IF och SM-lag behålls.
const decapitalize = (word: string) => {
  const second = word.charAt(1);
  if (second && second !== second.toLocaleLowerCase('sv')) return word;
  return word.charAt(0).toLocaleLowerCase('sv') + word.slice(1);
};

// Kunden skriver benämningarna själv och de hamnar i artikelns HTML.
const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// En tagg som skrivs för hand sparas av TinyMCE med å, ä och ö som namngivna entiteter ([f&ouml;rening]); en tagg
// från menyn sparas som den är. normalizeHelpPageTags gör om dem när artikeln läses in och sparas, men äldre innehåll
// kan ha båda formerna.
const namedEntities: Record<string, string> = {
  å: '&aring;',
  ä: '&auml;',
  ö: '&ouml;',
  Å: '&Aring;',
  Ä: '&Auml;',
  Ö: '&Ouml;',
};

const tagForms = (tag: string) => {
  const literal = `[${tag}]`;
  const encoded = literal.replace(/[åäöÅÄÖ]/g, (c) => namedEntities[c]);
  return encoded === literal ? [literal] : [literal, encoded];
};

const allTags = helpPageTerms.flatMap(({ tag }) => [tag, capitalize(tag)]);

/**
 * Skriver taggarna med vanliga tecken ([f&ouml;rening] blir [förening]). Editorn visar en låst tagg precis som den står
 * i HTML:en, så utan detta syns entiteten i taggen efter att den skrivits för hand och sparats.
 */
export const normalizeHelpPageTags = (html: string) =>
  allTags.reduce((result, tag) => {
    const [literal, encoded] = tagForms(tag);
    return encoded ? result.split(encoded).join(literal) : result;
  }, html);

// Varje begrepp i två varianter: taggen och värdet med liten respektive stor bokstav.
const getTermVariants = (mainSettings: MainSettingsItem[]) =>
  helpPageTerms.flatMap(({ tag, key, defaultLabel }) => {
    const value = getMainSetting(mainSettings, key, defaultLabel).trim() || defaultLabel;
    return [
      { tag, value: decapitalize(value) },
      { tag: capitalize(tag), value: capitalize(value) },
    ];
  });

/** Menyposterna i artikeleditorn. Etiketten visar vad taggen blir för den inloggade kunden. */
export const getHelpPageTermFields = (mainSettings: MainSettingsItem[]) =>
  getTermVariants(mainSettings).map(({ tag, value }) => ({ label: `[${tag}] (${value})`, value: `[${tag}]` }));

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Okända taggar lämnas orörda. Allt byts i ett svep, så en benämning som själv ser ut som en tagg byts inte en gång till.
const replaceTerms = (text: string, mainSettings: MainSettingsItem[], format: (value: string) => string) => {
  const replacements = new Map<string, string>();
  getTermVariants(mainSettings).forEach(({ tag, value }) => tagForms(tag).forEach((form) => replacements.set(form, format(value))));
  const pattern = new RegExp(Array.from(replacements.keys()).map(escapeRegExp).join('|'), 'g');

  return text.replace(pattern, (match) => replacements.get(match) ?? match);
};

/** Byter taggarna i en artikels HTML mot kundens benämningar, HTML-kodade. */
export const replaceHelpPageTerms = (html: string, mainSettings: MainSettingsItem[]) => replaceTerms(html, mainSettings, escapeHtml);

/** Byter taggarna i ren text, till exempel en kategoris beskrivning, som React sedan kodar själv. */
export const replaceHelpPageTermsInText = (text: string, mainSettings: MainSettingsItem[]) => replaceTerms(text, mainSettings, (value) => value);
